/* Én sak i LOS Marine Workspace som levende replika: sakhodet med fanene Oversikt · Arbeidsomfang · Kalkyle · Tilbud,
   og «MS Nordlys» som går fra kalkyle til kontrollert tilbud. Statiske data, samme formspråk som appen. */
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Check, ChevronDown, Download, FileText, MoreHorizontal, Share2, ShieldCheck, Sparkles } from "lucide-react";
import { Segmented } from "@/components/layout/Segmented";
import { QuoteView } from "@/components/canvas/QuoteView";
import { useCountUp } from "../motion";
import { geoFor } from "../Stage";
import { Avatar, Ghost, Primary, Shell, WorkPanel } from "./LosShell";
import { CASE, CONTROLLER, GROUPS, GROUP_NAME, ITEMS, QUOTE_TEXT_META, QUOTE_VM, USER, nok } from "./losMockData";

/* manuset: ett slag per steg */
export const SCRIPT = [
  { tab: "oversikt", stage: "kalkyle", ms: 4200 },
  { tab: "omfang", stage: "kalkyle", ms: 4600, toast: ["3 avklaringer sendt til Nordvest Rederi", "Anoder, sjøvannsventiler og kaistrøm – svar ventes før kalkylen låses"] },
  { tab: "kalkyle", stage: "kalkyle", ms: 4800, toast: ["Kalkylen er komplett – DB 17,3 %", "28 poster · prisliste 2026 · satser v1 · kapasitet uke 12–14 bekreftet"] },
  { tab: "tilbud", stage: "tilbud", ms: 4600, toast: ["Versjon 1 frosset fra kalkylen", "1 912 400 kr eks. mva · følgebrev og forbehold fra tekstbiblioteket"] },
  { tab: "tilbud", stage: "kontroll", ms: 5400, toast: [`Versjon 1 kontrollert av ${CONTROLLER.name}`, "Risikosjekkliste komplett · fullmakt: daglig leder · klar til utsending"] },
];

const STATUS = { kalkyle: "Kalkuleres", tilbud: "Tilbud klart til kontroll", kontroll: "Kontrollert · klar til utsending" };
const TABS = [
  { key: "oversikt", label: "Oversikt" },
  { key: "omfang", label: "Arbeidsomfang" },
  { key: "kalkyle", label: "Kalkyle" },
  { key: "tilbud", label: "Tilbud" },
  { key: "meldinger", label: <>Meldinger <span className="rounded-full bg-black/[0.07] px-1.5 text-[11px] tabular-nums text-foreground/70">2</span></>, divider: true },
];

/* ------------------------------------------------------------------ hodet */

const Head = ({ tab, onTab, stage, nar, layoutId }) => (
  <header className={`grid shrink-0 items-center gap-3 border-b border-black/[0.05] bg-white/90 ${nar ? "h-auto grid-cols-1 px-3 py-2" : "h-[60px] grid-cols-[1fr_auto_1fr] px-4"}`}>
    <div className="min-w-0 leading-tight">
      <div className="truncate text-[15.5px] font-semibold text-foreground">{CASE.name}</div>
      <div className="truncate text-[12.5px] text-muted-foreground">{CASE.customer} · {STATUS[stage]}</div>
    </div>
    <Segmented value={tab} onChange={onTab} items={TABS.map((t) => ({ ...t, testId: `los-sak-tab-${t.key}` }))} size={nar ? "sm" : "md"} layoutId={layoutId} ariaLabel="Faner" scroll={nar} className={nar ? "max-w-full" : ""} />
    {!nar && (
      <div className="flex items-center justify-end gap-1.5">
        <Ghost icon={Sparkles}>Assistent <span className="rounded-full bg-primary px-1.5 font-mono text-[10px] font-semibold text-white">1</span></Ghost>
        <Ghost icon={Share2}>Del</Ghost>
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full text-foreground/70"><MoreHorizontal className="h-4 w-4" /></span>
      </div>
    )}
  </header>
);

/* ------------------------------------------------------------------ Oversikt */

const Facts = () => (
  <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-y border-black/[0.08] py-4 text-[13px] sm:grid-cols-3">
    {[["Kunde", CASE.customer], ["Fartøy", CASE.vessel], ["Verkstedopphold", CASE.period_text], ["Dokkdager", CASE.repair_days], ["Tilbudsfrist", CASE.deadline], ["Kontakt", CASE.contact]].map(([l, v]) => (
      <div key={l} className="min-w-0"><dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">{l}</dt><dd className="mt-0.5 truncate font-medium text-foreground/90">{v}</dd></div>
    ))}
  </dl>
);

const OversiktBody = ({ stage, nar }) => {
  const done = stage === "kontroll";
  return (
    <div className={`mx-auto w-full max-w-[860px] ${nar ? "px-4 py-5" : "px-8 py-7"}`}>
      <div className="flex items-center justify-between gap-4 rounded-[14px] border border-black/[0.06] bg-[hsl(var(--sidebar))] px-4 py-3.5">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Neste steg</div>
          <div className="mt-0.5 truncate text-[14.5px] font-semibold text-foreground">{done ? "Send tilbudet til Nordvest Rederi" : stage === "tilbud" ? "Send versjon 1 til kontroll" : "Fortsett kalkylen · 722.1 Brannpumpe – velg prisform"}</div>
        </div>
        <Primary icon={ArrowRight}>{done ? "Send" : stage === "tilbud" ? "Til kontroll" : "Fortsett"}</Primary>
      </div>
      <div className={`mt-6 grid gap-5 ${nar ? "grid-cols-1" : "grid-cols-3"}`}>
        {[
          ["Arbeidsomfang", "28 poster", "5 avklaringer · 3 besvart"],
          ["Kalkyle", done || stage === "tilbud" ? "Komplett" : "26 av 28 ferdig", `${CASE.sumText} · DB ${CASE.dbPct}`],
          ["Tilbud", done ? "Versjon 1 kontrollert" : stage === "tilbud" ? "Versjon 1 · utkast" : "Ingen versjon ennå", done ? `av ${CONTROLLER.name}` : `frist ${CASE.deadline}`],
        ].map(([k, v, s]) => (
          <div key={k}>
            <div className="k-label">{k}</div>
            <div className="mt-1 text-[17px] font-semibold tracking-[-0.01em] text-foreground">{v}</div>
            <div className="mt-0.5 text-[12.5px] text-muted-foreground">{s}</div>
          </div>
        ))}
      </div>
      <div className="mt-7"><Facts /></div>
      <div className="mt-7">
        <div className="k-label">Slik ble arbeidet opprettet</div>
        <p className="mt-1.5 text-[14px] leading-[1.6] text-foreground/85">Fra e-post fra {CASE.contact} i dag 09:14. Assistenten leste 3 dokumenter og la inn 28 arbeidsposter med SFI- og WO-nummer – kundens tekst er beholdt urørt ved siden av LOS Marines arbeidsomfang.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {["Yard_jobs_export_MS_Nordlys.pdf · 114 s.", "GA_MS_Nordlys_rev3.pdf", "Bilder_skrog_2026.zip"].map((f) => (
            <span key={f} className="inline-flex h-8 items-center gap-1.5 rounded-full border border-black/[0.08] bg-white px-3 text-[12.5px] text-foreground/80"><FileText className="h-3.5 w-3.5 text-muted-foreground" /> {f}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ Arbeidsomfang */

const SEL = "309.1";

const OmfangBody = ({ nar }) => {
  const sel = ITEMS.find((i) => i.ref === SEL);
  return (
    <div className="flex h-full min-h-0">
      <div className={`mb-noscroll min-w-0 flex-1 overflow-hidden ${nar ? "px-3 py-4" : "px-7 py-6"}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="text-[13px] text-muted-foreground"><span className="font-semibold text-foreground">28 poster</span> · 26 klare · 2 avklares</div>
          {!nar && <div className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground"><span className="rounded-full bg-black/[0.05] px-2.5 py-1">Alle</span><span className="px-2 py-1">Avklares</span><span className="px-2 py-1">Dokumenter 3</span></div>}
        </div>
        <div className="mt-4 space-y-5">
          {GROUPS.map((g) => (
            <div key={g.key}>
              <div className="flex items-center gap-2 border-b border-black/[0.08] pb-1.5 text-[12px] font-semibold text-foreground/80"><span>{g.name}</span><span className="text-muted-foreground">{ITEMS.filter((i) => i.group === g.key).length}</span></div>
              <ul className="divide-y divide-black/[0.05]">
                {ITEMS.filter((i) => i.group === g.key).map((it) => {
                  const on = it.ref === SEL;
                  const open = it.ref === "251.1" || it.ref === "100.1";
                  return (
                    <li key={it.ref} className={`k-row flex items-center gap-3 px-2 ${on ? "rounded-[10px] bg-[hsl(var(--primary)/0.07)]" : ""}`}>
                      <span className="k-ref w-[52px] shrink-0">{it.ref}</span>
                      <span className="k-title min-w-0 flex-1 truncate">{it.title}</span>
                      {open ? <span className="inline-flex h-6 items-center rounded-full bg-[hsl(var(--status-warn)/0.12)] px-2 text-[11px] font-medium text-[hsl(var(--status-warn))]">Avklares</span> : <Check className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-ok))]" strokeWidth={2.4} />}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
      {!nar && (
        <aside className="flex w-[440px] shrink-0 flex-col border-l border-black/[0.06] bg-white px-6 py-6">
          <div className="k-ref">{sel.ref} · SFI {sel.sfi} · WO {sel.wo}</div>
          <h2 className="mt-1 text-[18px] font-semibold leading-tight tracking-[-0.01em] text-foreground">{sel.title}</h2>
          <div className="mt-2 flex flex-wrap gap-1.5 text-[11.5px]">
            {["Fastpris", "Avd. 55 Rør", GROUP_NAME[sel.group], "Klasse"].map((x) => <span key={x} className="rounded-full border border-black/[0.08] px-2 py-0.5 text-foreground/70">{x}</span>)}
          </div>
          <div className="mt-6 grid grid-cols-2 gap-5">
            <div><div className="k-label">Kundens tekst</div><p className="mt-1.5 text-[13px] leading-[1.55] text-foreground/80">{sel.customer}</p><div className="mt-1.5 text-[11.5px] text-muted-foreground">Spes. s. 41 · urørt</div></div>
            <div><div className="k-label">LOS Marines arbeidsomfang</div><p className="mt-1.5 text-[13px] leading-[1.55] text-foreground/90">Demontering, rengjøring og innsliping av 12 sjøvannsventiler. Nye pakninger og trykktest i nærvær av klasse. Ventilene leveres tilbake malt og merket.</p></div>
          </div>
          <div className="mt-6">
            <div className="k-label">Avklaringer</div>
            <div className="mt-2 flex items-start gap-2.5 rounded-[10px] bg-[hsl(var(--sidebar))] px-3 py-2.5 text-[13px]">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-ok))]" strokeWidth={2.4} />
              <div><div className="text-foreground/90">Antall ventiler – spesifikasjonen sier 12, tegningen viser 14?</div><div className="mt-0.5 text-[12px] text-muted-foreground">Besvart av {CASE.contact} 2. nov: 12 ventiler. To er plugget.</div></div>
            </div>
          </div>
          <div className="mt-6">
            <div className="k-label">Delarbeider</div>
            <ul className="mt-2 divide-y divide-black/[0.05] text-[13px]">{(sel.kids || []).map((k, i) => <li key={k} className="flex items-center gap-3 py-2"><span className="k-ref w-[52px]">{sel.ref}.{i + 1}</span><span className="text-foreground/85">{k}</span></li>)}</ul>
          </div>
          <div className="mt-auto flex items-center gap-2 pt-6 text-[12.5px] text-muted-foreground"><Avatar size={22} /> Prises av Egil · Intern dialog · 1 notat</div>
        </aside>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ Kalkyle */

const StageDots = ({ stage }) => {
  const idx = stage === "kontroll" ? 2 : 1;
  return (
    <ol className="flex items-center gap-4 text-[12.5px]">
      {["Estimert", "Komplett", "Kontrollert"].map((l, i) => (
        <li key={l} className={`flex items-center gap-1.5 ${i <= idx ? "text-foreground" : "text-muted-foreground/70"}`}>
          {i < idx ? <Check className="h-3.5 w-3.5 text-[hsl(var(--status-ok))]" strokeWidth={2.4} /> : <span className={`h-2 w-2 rounded-full ${i === idx ? "bg-primary" : "border border-black/[0.25]"}`} />}
          <span className={i === idx ? "font-semibold" : "font-medium"}>{l}</span>
        </li>
      ))}
    </ol>
  );
};

const Line = ({ title, sub, right, tone }) => (
  <div className="flex items-start justify-between gap-3 border-b border-black/[0.06] py-3 last:border-0">
    <div className="min-w-0"><div className={`text-[14px] font-medium ${tone === "muted" ? "text-muted-foreground" : "text-foreground"}`}>{title}</div>{sub ? <div className="mt-0.5 text-[12.5px] text-muted-foreground">{sub}</div> : null}</div>
    {right ? <span className="shrink-0 text-[12.5px] text-muted-foreground">{right}</span> : <ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />}
  </div>
);

export const KalkyleBody = ({ stage, nar, still }) => {
  const ctrl = stage === "kontroll";
  return (
    <div className={`mx-auto w-full max-w-[1180px] ${nar ? "px-4 py-4" : "px-8 py-5"}`}>
      <div className="flex items-center justify-between gap-3">
        <Segmented value="summary" onChange={() => {}} items={[{ key: "summary", label: "Økonomi" }, { key: "items", label: "Arbeidsposter" }]} size="sm" layoutId={still ? "los-still-kalkyle-view" : "los-kalkyle-view"} ariaLabel="Visning" />
        <div className="flex items-center gap-1.5">
          <Primary>{ctrl ? "Åpne tilbudet" : "Gå til tilbudet"}</Primary>
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full text-foreground/70"><MoreHorizontal className="h-4 w-4" /></span>
        </div>
      </div>
      <div className={`mt-6 grid gap-x-12 gap-y-8 ${nar ? "grid-cols-1" : "grid-cols-[1fr_minmax(280px,34%)]"}`}>
        <div>
          <StageDots stage={stage} />
          <div className="mt-5 k-label">Fastprissum</div>
          <div className="k-hero mt-1 tabular-nums text-foreground" data-num="">{CASE.sumText}</div>
          <div className="k-meta mt-1">alle 28 poster er ferdig · 1 etter regning · 1 opsjon utenfor</div>
          <dl className={`mt-6 grid gap-x-8 gap-y-4 ${nar ? "grid-cols-2" : "grid-cols-4"}`}>
            {[["Kost", CASE.cost, "prisliste 2026 · satser v1"], ["Dekningsbidrag", CASE.db, CASE.dbPct], ["Timer", CASE.hours, "6 avdelinger"], ["Underleverandør", CASE.sub, "11 %"]].map(([k, v, s]) => (
              <div key={k}><dt className="k-label">{k}</dt><dd className="k-figure mt-0.5 tabular-nums text-foreground" data-num="">{v}</dd><dd className="k-meta mt-0.5">{s}</dd></div>
            ))}
          </dl>
          <table className="mt-7 w-full border-collapse text-[13.5px]">
            <thead><tr className="text-[11.5px] font-medium text-muted-foreground"><th className="pb-2 text-left font-medium">Fagområde</th><th className="pb-2 text-right font-medium">Fremdrift</th><th className="pb-2 text-right font-medium">DB %</th><th className="pb-2 text-right font-medium">Salg</th></tr></thead>
            <tbody className="divide-y divide-black/[0.06]">
              {GROUPS.map((g) => (
                <tr key={g.key} className="k-row">
                  <td className="py-2.5 font-medium text-foreground"><span className="inline-flex items-center gap-2">{g.name}<Check className="h-3.5 w-3.5 text-[hsl(var(--status-ok))]" strokeWidth={2.4} /></span></td>
                  <td className="py-2.5 text-right tabular-nums text-foreground/80">{g.done} av {g.total}</td>
                  <td className="py-2.5 text-right tabular-nums text-foreground/80">{g.db}</td>
                  <td className="k-amount py-2.5 text-right tabular-nums text-foreground">{nok(g.sum)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-8 k-label">Utenfor fastprissummen</div>
          <Line title="1 post etter regning" sub="722.1 Brannpumpe – faktureres etter medgått tid" right={<><span className="mr-1.5 text-[11.5px]">anslag</span><span className="font-medium text-foreground">8 750 kr</span></>} />
          <Line title="1 opsjon" sub="862.1 Landstrømskap – ny tavle" right={<span className="font-medium text-foreground">118 000 kr</span>} />
          <div className="mt-8 flex items-center justify-between"><span className="k-label">Tilbud</span><span className="text-[13px] font-medium text-[hsl(var(--navy))]">Åpne tilbudet</span></div>
          <Line title={ctrl ? "Versjon 1 · kontrollert" : stage === "tilbud" ? "Versjon 1 · utkast" : "Ingen versjon ennå"} sub={ctrl ? `${CONTROLLER.name} · i dag` : "Kalkylen er komplett – versjonen kan fryses"} right={<span className="font-medium text-foreground">{CASE.sumText}</span>} />
        </div>
        <aside>
          <div className="k-label">Trenger oppmerksomhet</div>
          <div className="mt-3 flex items-baseline justify-between"><span className="text-[15px] font-semibold text-foreground">28 av 28 poster ferdig</span><span className="text-[12.5px] text-muted-foreground tabular-nums">100 %</span></div>
          <div className="mt-2 h-[3px] w-full rounded-full bg-black/[0.07]"><div className="h-full w-full rounded-full bg-[hsl(var(--navy))]" /></div>
          <div className="mt-1.5 text-[12px] text-muted-foreground">26 fastpris · 1 etter regning · 1 opsjon</div>
          <div className="mt-4">
            <Line title="Alle poster er ferdig" sub="Kalkylen kan gå videre til tilbudet" />
            <Line title={<>Risiko <span className="ml-1.5 inline-flex h-6 items-center gap-1 rounded-full bg-[hsl(var(--status-ok)/0.12)] px-2 text-[11.5px] font-medium text-[hsl(var(--status-ok))]"><ShieldCheck className="h-3 w-3" /> Sjekkliste komplett</span></>} sub="Påkrevd over 500 000 kr · 7 av 7 besvart · aktuell" />
            <Line title="Kapasitet · uke 12–14 kan loves" sub="Flytedokk ledig · Maskin 92 % i uke 13 – reservasjon lagt inn" />
            <Line title="Kvalitetssjekk · 2 funn" sub="i dag · ingen under laveste DB-standard" />
            <Line title="7 linjer med simulert kost" sub="satser uten bekreftet kost – vises i grunnlaget, aldri i tilbudet" tone="muted" />
          </div>
        </aside>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ Tilbud */

const TOC = ["Følgebrev", "Om leveransen", "Leveranse og priser", "Opsjoner", "Forutsetninger", "Forbehold og avgrensninger", "Generelle forutsetninger", "Vilkår og betingelser", "Gyldighet", "Avslutning"];

const TilbudBody = ({ stage, nar }) => {
  const ctrl = stage === "kontroll";
  return (
    <div className="flex h-full min-h-0 flex-col bg-[hsl(210_22%_95%)]">
      <div className={`shrink-0 border-b border-black/[0.05] bg-white ${nar ? "px-4 py-3" : "px-7 pb-3 pt-4"}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[20px] font-semibold tracking-[-0.01em] text-foreground">Tilbud {CASE.number} <span className={`inline-flex items-center gap-1 text-[14px] font-medium ${ctrl ? "text-[hsl(var(--status-ok))]" : "text-muted-foreground"}`}>{ctrl ? <><ShieldCheck className="h-3.5 w-3.5" /> Versjon 1 · Kontrollert</> : stage === "tilbud" ? "Versjon 1" : "Utkast"} <ChevronDown className="h-3.5 w-3.5" /></span></div>
            <div className="mt-0.5 text-[12.5px] text-muted-foreground">{CASE.customer} · {CASE.vessel}</div>
          </div>
          {!nar && (
            <div className="flex items-center gap-2">
              <Segmented value="rediger" onChange={() => {}} items={[{ key: "rediger", label: "Rediger" }, { key: "forhandsvis", label: "Forhåndsvis" }, { key: "endringer", label: "Endringer" }]} size="sm" layoutId="los-tilbud-mode" ariaLabel="Modus" />
              <Ghost icon={Download}>PDF</Ghost>
              <Primary>{ctrl ? "Send til kunde" : stage === "tilbud" ? "Send til kontroll" : "Lag versjon 1"}</Primary>
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full text-foreground/70"><MoreHorizontal className="h-4 w-4" /></span>
            </div>
          )}
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-muted-foreground">
          <span>Sum eks. mva <span className="font-semibold tabular-nums text-foreground">{CASE.sumText}</span></span><span>·</span><span>28 poster</span><span>·</span><span>1 opsjon utenfor sum</span><span>·</span><span>{ctrl ? `Kontrollert av ${CONTROLLER.name}` : `Sist endret i dag av ${USER.name}`}</span>
        </div>
        <div className="mt-2.5 rounded-[10px] border border-black/[0.06] bg-[hsl(var(--sidebar))] px-3.5 py-2 text-[13px] text-foreground/85">{ctrl ? "Versjonen er kontrollert og uendret siden. Tilbudet kan sendes." : "Risikosjekklisten er komplett og kapasiteten for uke 12–14 er bekreftet. Versjon 1 kan sendes til kontroll."}</div>
      </div>
      <div className={`mb-noscroll flex min-h-0 flex-1 gap-6 overflow-hidden ${nar ? "px-3 py-4" : "px-7 py-6"}`}>
        {!nar && (
          <nav className="w-[180px] shrink-0 text-[12.5px]">
            <ul className="space-y-1.5">{TOC.map((t, i) => <li key={t} className={`flex items-center justify-between ${i === 0 ? "font-medium text-foreground" : "text-foreground/65"}`}>{t}{i === 3 ? <span className="h-1.5 w-1.5 rounded-full border border-black/[0.2]" /> : null}</li>)}</ul>
            <div className="mt-6 border-t border-black/[0.08] pt-4 k-label">Versjoner</div>
            <ul className="mt-2 space-y-2">
              <li className="flex items-start gap-2"><span className="mt-1.5 h-2 w-2 rounded-full bg-[hsl(var(--navy))]" /><div><div className="font-medium text-foreground">Utkast</div><div className="text-[11.5px] text-muted-foreground">følger kalkylen</div></div></li>
              {stage !== "kalkyle" && <li className="flex items-start gap-2"><span className={`mt-1.5 h-2 w-2 rounded-full ${ctrl ? "bg-[hsl(var(--status-ok))]" : "border border-black/[0.3]"}`} /><div><div className="font-medium text-foreground">Versjon 1 <span className="ml-1 font-mono text-[11px] text-muted-foreground">1 912 400</span></div><div className="text-[11.5px] text-muted-foreground">{ctrl ? `kontrollert · ${CONTROLLER.initials}` : "i dag · Egil"}</div></div></li>}
            </ul>
          </nav>
        )}
        <div className="min-w-0 flex-1">
          <div className="rounded-[18px] bg-white shadow-[0_1px_2px_hsl(212_40%_16%/0.06),0_24px_60px_-30px_hsl(212_40%_16%/0.35)]">
            <QuoteView vm={QUOTE_VM} wide={!nar} status={ctrl ? "kontrollert" : "utkast"} texts={QUOTE_TEXT_META} customerView={false} />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ saken */

const BODY = { oversikt: OversiktBody, omfang: OmfangBody, kalkyle: KalkyleBody, tilbud: TilbudBody };

export const LosSak = ({ beat, tab, onTab = () => {}, mode = "full", still = false }) => {
  const Body = BODY[tab] || OversiktBody;
  const nar = mode === "narrow";
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden" data-testid="los-sak" data-tab={tab} data-stage={beat.stage}>
      <Head tab={tab} onTab={onTab} stage={beat.stage} nar={nar} layoutId={still ? "los-still-tabs" : "los-sak-tabs"} />
      <div key={tab + beat.stage} className="mb-fade mb-noscroll min-h-0 flex-1 overflow-hidden"><Body stage={beat.stage} nar={nar} still={still} /></div>
    </div>
  );
};

export const LosSakApp = ({ beat, tab, onTab, mode = "full", still = false }) => (
  <Shell mode={mode} active="arbeid" panel={<WorkPanel />} title="Arbeid" toast={beat.toast} testId="los-sak-app" view="sak">
    <LosSak beat={beat} tab={tab} onTab={onTab} mode={mode} still={still} />
  </Shell>
);

/* kjører manuset; et klikk på en fane tar over en stund */
export const useScript = (active) => {
  const [i, setI] = useState(0);
  const [manual, setManual] = useState(null);
  useEffect(() => {
    if (!active || manual) return undefined;
    const t = setTimeout(() => setI((x) => (x + 1) % SCRIPT.length), SCRIPT[i].ms);
    return () => clearTimeout(t);
  }, [i, active, manual]);
  useEffect(() => {
    if (!manual) return undefined;
    const t = setTimeout(() => setManual(null), 9000);
    return () => clearTimeout(t);
  }, [manual]);
  const beat = SCRIPT[i];
  return { beat: manual ? { ...beat, toast: null } : beat, tab: manual || beat.tab, onTab: setManual, i };
};

/* et stillbilde av saken – ingen manus, ingen toast – lagt ut for sin egen bredde og skalert ned (heroen) */
export const LosStill = ({ tab = "kalkyle", stage = "kalkyle", url = "workspace.los-marine.no/p/26112?tab=kalkyle", caption, testId = "tilbod-hero-still" }) => {
  const ref = useRef(null);
  const [geo, setGeo] = useState({ mode: "full", vw: 1360, s: 0.86 });
  const [active, setActive] = useState(false);
  useEffect(() => {
    const el = ref.current;
    const ro = new ResizeObserver(([e]) => setGeo(geoFor(e.contentRect.width)));
    ro.observe(el);
    const io = new IntersectionObserver((entries) => setActive(entries[entries.length - 1].isIntersecting), { threshold: 0.15 });
    io.observe(el);
    return () => {
      ro.disconnect();
      io.disconnect();
    };
  }, []);
  useCountUp(ref, active, { delay: 700 });
  const ih = geo.mode === "narrow" ? 600 : 820;
  return (
    <div className="mb-frame" data-testid={testId} data-mode={geo.mode} data-active={active ? "1" : "0"} aria-hidden="true">
      <div className="mb-chrome">
        <span className="mb-dots"><i /><i /><i /></span>
        <span className="mb-url">{url}</span>
        <span className="mb-cap">{caption}</span>
      </div>
      <div ref={ref} className="mb-view pointer-events-none select-none" style={{ height: ih * geo.s }}>
        <div className="mb-scale mb-cine" style={{ transform: `scale(${geo.s})`, width: geo.vw, height: ih }}>
          <LosSakApp beat={{ tab, stage, ms: 0 }} tab={tab} mode={geo.mode} still />
        </div>
      </div>
    </div>
  );
};
